package fyi.moko.hotpl.ui.detail

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.produceState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fyi.moko.hotpl.domain.model.PhotoGroup
import fyi.moko.hotpl.ui.placeform.decodeImageBitmap

/**
 * 방문을 기록하며 올린 사진들과 **각 사진이 어디로 갈지**를 보여준다.
 *
 * 화면은 분류 이름(`외관`, `메뉴판`…)을 말하지 않는다. 사용자에게 중요한 건 이 사진이 가게
 * 카드에 계속 보이는지, 오늘 기록에만 남는지이기 때문이다. 칩을 누르면 반대쪽으로 옮겨진다.
 */
@Composable
fun VisitPhotoSection(
    photos: List<PendingVisitPhoto>,
    classifying: Boolean,
    onAddPhotos: () -> Unit,
    onCapturePhoto: () -> Unit,
    cameraAvailable: Boolean,
    onPhotoGroupChange: (Int, PhotoGroup) -> Unit,
    onPhotoClick: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text("Photos", style = MaterialTheme.typography.labelLarge)
            val canAdd = !classifying && photos.size < DetailViewModel.MAX_VISIT_PHOTOS
            // 방문 기록은 **가게에 서서 쓰는 화면**이라 카메라가 갤러리보다 앞선다. 편집
            // 화면에는 있고 여기에는 없던 것을 리뷰어가 짚었다(2026-09-09 피드백 R-12).
            if (cameraAvailable) {
                TextButton(onClick = onCapturePhoto, enabled = canAdd) { Text("Take photo") }
            }
            TextButton(onClick = onAddPhotos, enabled = canAdd) {
                Text(if (photos.isEmpty()) "Add" else "Add more")
            }
            if (classifying) {
                Text(
                    "Sorting…",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }

        if (photos.isEmpty()) {
            Text(
                "Photos you add get split between the place's photos and today's entry.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            return@Column
        }

        Row(
            modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            photos.forEachIndexed { index, photo ->
                PendingPhotoTile(
                    photo = photo,
                    onToggleDestination = {
                        val moved = if (photo.destination == PhotoGroup.PLACE) {
                            PhotoGroup.VISIT
                        } else {
                            PhotoGroup.PLACE
                        }
                        onPhotoGroupChange(index, moved)
                    },
                    onClick = { onPhotoClick(index) },
                )
            }
        }

        // 분류기가 아무것도 못 정한 사진이 있으면 그것만 짚어 준다. 전부 자동으로 갈렸을 때는
        // 아무 말도 하지 않는다 — 매번 확인을 요구하면 사진 여러 장 올리는 값이 사라진다.
        val undecided = photos.count { it.undecided }
        if (undecided > 0) {
            Text(
                undecidedPhotoNotice(undecided),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun PendingPhotoTile(
    photo: PendingVisitPhoto,
    onToggleDestination: () -> Unit,
    onClick: () -> Unit,
) {
    // 디코딩은 사진마다 한 번만. 바이트가 같으면 다시 만들지 않는다.
    val bitmap by produceState<androidx.compose.ui.graphics.ImageBitmap?>(null, photo.bytes) {
        value = decodeImageBitmap(photo.bytes)
    }

    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Box(
            modifier = Modifier.size(72.dp).clip(RoundedCornerShape(10.dp))
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .clickable(onClick = onClick),
        ) {
            bitmap?.let {
                Image(
                    bitmap = it,
                    contentDescription = "Photo options",
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop,
                )
            }
            if (photo.undecided) {
                Box(
                    modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.35f)),
                    contentAlignment = Alignment.Center,
                ) {
                    Text("?", color = Color.White, style = MaterialTheme.typography.titleMedium)
                }
            }
        }
        TextButton(onClick = onToggleDestination, contentPadding = androidx.compose.foundation.layout.PaddingValues(2.dp)) {
            Text(
                text = if (photo.destination == PhotoGroup.PLACE) "Place" else "Today",
                style = MaterialTheme.typography.labelSmall,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(horizontal = 2.dp),
            )
        }
    }
}
